<template>
  <div class="access-page">
    <div class="access-card">
      <h2>Доступы</h2>
      <p class="hint">Владелец видит все типы встреч своей команды. Участник видит только отмеченные типы. Администратор видит все команды.</p>
      <p v-if="error" class="error">{{ error }}</p>
      <div v-if="!canManage" class="hint">Управлять доступом могут администратор и владельцы команд.</div>
      <template v-else>
        <q-select
            outlined
            emit-value
            map-options
            v-model="selectedTeamId"
            :options="teamOptions"
            label="Команда"
            @update:model-value="loadMembers"
        />
        <div v-if="selectedTeamId" class="add-user">
          <q-input outlined dense v-model="email" label="Email пользователя" />
          <q-select outlined dense emit-value map-options v-model="newRole" :options="roleOptions" label="Роль" />
          <q-btn color="primary" label="Добавить" :loading="saving" @click="addUser" />
        </div>
        <q-table
            v-if="selectedTeamId"
            flat
            bordered
            title="Участники"
            :rows="members"
            :columns="columns"
            row-key="email"
            :pagination="pagination"
        >
          <template v-slot:body="props">
            <q-tr :props="props">
              <q-td key="email" :props="props">{{ props.row.user.email }}</q-td>
              <q-td key="name" :props="props">{{ props.row.user.name }}</q-td>
              <q-td key="role" :props="props">
                <q-select
                    dense
                    outlined
                    emit-value
                    map-options
                    :model-value="props.row.role"
                    :options="roleOptions"
                    @update:model-value="changeRole(props.row, $event)"
                />
              </q-td>
              <q-td key="types" :props="props">
                <div v-if="props.row.role === 'owner'" class="hint">все типы встреч</div>
                <div v-else class="type-list">
                  <q-checkbox
                      v-for="meetingType in meetingTypes"
                      :key="meetingType.id"
                      :model-value="props.row.meetingTypeIds.includes(meetingType.id)"
                      :label="meetingType.name"
                      @update:model-value="toggleMeetingType(props.row, meetingType.id, $event)"
                  />
                </div>
              </q-td>
              <q-td key="actions" :props="props">
                <q-btn flat color="negative" label="Убрать" @click="removeUser(props.row)" />
                <q-checkbox
                    v-if="currentUser && currentUser.isAdmin"
                    :model-value="props.row.user.isAdmin"
                    label="Админ"
                    @update:model-value="toggleAdmin(props.row, $event)"
                />
              </q-td>
            </q-tr>
          </template>
        </q-table>
      </template>
    </div>
  </div>
</template>

<script>
import grpc from "@/services/grpc";
import {useTokenStore} from "@/stores/token";

export default {
  name: 'AccessManager',
  data() {
    return {
      grpcClient: new grpc(),
      teams: [],
      selectedTeamId: null,
      members: [],
      meetingTypes: [],
      email: '',
      newRole: 'member',
      error: '',
      saving: false,
      roleOptions: [
        {label: 'Участник', value: 'member'},
        {label: 'Владелец', value: 'owner'},
      ],
      columns: [
        {name: 'email', label: 'Email', field: row => row.user.email, align: 'left'},
        {name: 'name', label: 'Имя', field: row => row.user.name, align: 'left'},
        {name: 'role', label: 'Роль', field: 'role', align: 'left'},
        {name: 'types', label: 'Типы встреч', field: 'meetingTypeIds', align: 'left'},
        {name: 'actions', label: '', field: 'actions', align: 'left'},
      ],
      pagination: {rowsPerPage: 0},
    };
  },
  computed: {
    currentUser() {
      return useTokenStore().user;
    },
    canManage() {
      const user = this.currentUser;
      if (!user) {
        return false;
      }
      return user.isAdmin || (user.teams || []).some(team => team.role === 'owner');
    },
    teamOptions() {
      return this.teams.map(team => ({label: team.name, value: team.id}));
    },
  },
  async created() {
    await this.loadTeams();
  },
  methods: {
    async loadTeams() {
      this.error = '';
      const user = this.currentUser;
      if (!user) {
        return;
      }
      try {
        const teams = await this.grpcClient.getTeams();
        const visible = [];
        for (const team of teams) {
          const owned = (user.teams || []).some(item => item.teamId === team.getId() && item.role === 'owner');
          if (user.isAdmin || owned) {
            visible.push({id: team.getId(), name: team.getName()});
          }
        }
        this.teams = visible;
        if (visible.length > 0) {
          this.selectedTeamId = visible[0].id;
          await this.loadMembers();
        }
      } catch (err) {
        this.error = err.message || 'Не удалось загрузить команды';
      }
    },
    async loadMembers() {
      if (!this.selectedTeamId) {
        this.members = [];
        return;
      }
      this.error = '';
      try {
        const members = await this.grpcClient.getTeamAccessList(this.selectedTeamId);
        this.members = members.map(member => ({
          ...member,
          email: member.user.email,
        }));
        const meetingTypes = await this.grpcClient.getMeetingTypesSchedule(this.selectedTeamId);
        this.meetingTypes = meetingTypes;
      } catch (err) {
        this.error = err.message || 'Не удалось загрузить доступы';
      }
    },
    async addUser() {
      if (!this.email || !this.selectedTeamId) {
        return;
      }
      this.saving = true;
      this.error = '';
      try {
        const user = await this.grpcClient.findUserByEmail(this.email);
        await this.grpcClient.grantTeamAccess(user.id, this.selectedTeamId, this.newRole);
        this.email = '';
        await this.loadMembers();
      } catch (err) {
        this.error = err.message || 'Не удалось добавить пользователя';
      } finally {
        this.saving = false;
      }
    },
    async changeRole(member, role) {
      this.error = '';
      try {
        await this.grpcClient.grantTeamAccess(member.user.id, this.selectedTeamId, role);
        await this.loadMembers();
      } catch (err) {
        this.error = err.message || 'Не удалось изменить роль';
      }
    },
    async toggleMeetingType(member, meetingTypeId, granted) {
      this.error = '';
      try {
        if (granted) {
          await this.grpcClient.grantMeetingTypeAccess(member.user.id, meetingTypeId);
          if (!member.meetingTypeIds.includes(meetingTypeId)) {
            member.meetingTypeIds.push(meetingTypeId);
          }
        } else {
          await this.grpcClient.revokeMeetingTypeAccess(member.user.id, meetingTypeId);
          member.meetingTypeIds = member.meetingTypeIds.filter(id => id !== meetingTypeId);
        }
      } catch (err) {
        this.error = err.message || 'Не удалось изменить доступ к типу встречи';
        await this.loadMembers();
      }
    },
    async removeUser(member) {
      this.error = '';
      try {
        await this.grpcClient.revokeTeamAccess(member.user.id, this.selectedTeamId);
        await this.loadMembers();
      } catch (err) {
        this.error = err.message || 'Не удалось убрать пользователя';
      }
    },
    async toggleAdmin(member, isAdmin) {
      this.error = '';
      try {
        await this.grpcClient.setUserAdmin(member.user.id, isAdmin);
        member.user.isAdmin = isAdmin;
      } catch (err) {
        this.error = err.message || 'Не удалось изменить права администратора';
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.access-page {
  padding: 28px 20px 48px;
  background: #f6f8fb;
}
.access-card {
  width: min(1120px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5eaf2;
  border-radius: 8px;
  text-align: left;
  h2 {
    margin: 0;
  }
}
.add-user {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  > * {
    flex: 1 1 180px;
  }
}
.type-list {
  display: flex;
  flex-direction: column;
}
.hint {
  color: #546179;
  margin: 0;
}
.error {
  color: #c10015;
  margin: 0;
}
</style>
